import { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";

const FEATURES = [
  { icon: "\u{1F4C7}", label: "Business Card Intelligence",  desc: "AI-powered contact extraction & research" },
  { icon: "\u{1F4CB}", label: "Bid Compliance Analysis",     desc: "11-point RFQ compliance scoring" },
  { icon: "\u{1F4B0}", label: "Personal Expense Tracking",   desc: "Receipt OCR & automated categorisation" },
  { icon: "\u{1F4E7}", label: "Email & Tender Extraction",   desc: "Actionable intelligence from your inbox" },
];

const CSS = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg:       #f0f4fc;
  --panel:    #f8faff;
  --card:     #f8faff;
  --border:   #d5e3f5;
  --border2:  #eff6ff;
  --accent:   #2563eb;
  --violet:   #6d28d9;
  --text:     #0d1a36;
  --text2:    #8ca8d0;
  --muted:    #6080aa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color-scheme: light;
}
html, body, #root { height: 100%; }
.lr { display: flex; min-height: 100vh; background: var(--bg); color: var(--text); }

/* LEFT */
.lr-left {
  flex: 0 0 520px;
  display: flex; flex-direction: column;
  padding: 48px 52px;
  background: var(--panel);
  border-right: 1px solid var(--border2);
  position: relative; overflow: hidden;
}
.lr-left::before {
  content: ''; position: absolute;
  top: -120px; left: -80px; width: 480px; height: 480px;
  background: radial-gradient(circle, rgba(64,119,244,.13) 0%, transparent 70%);
  pointer-events: none;
}
.lr-left::after {
  content: ''; position: absolute;
  bottom: -60px; right: -60px; width: 360px; height: 360px;
  background: radial-gradient(circle, rgba(109,94,245,.09) 0%, transparent 70%);
  pointer-events: none;
}
.lr-lines {
  position: absolute; inset: 0;
  background-image: repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(255,255,255,.022) 39px, rgba(255,255,255,.022) 40px);
  pointer-events: none;
}
.lr-brand { display: flex; align-items: center; gap: 12px; position: relative; z-index: 1; }
.lr-brand-mark {
  width: 40px; height: 40px; border-radius: 10px;
  background: linear-gradient(140deg, #2563eb 0%, #6d28d9 100%);
  display: flex; align-items: center; justify-content: center; font-size: 20px;
  box-shadow: 0 0 0 1px rgba(64,119,244,.3), 0 4px 16px rgba(64,119,244,.25);
  flex-shrink: 0;
}
.lr-brand-name { font-size: 15px; font-weight: 700; letter-spacing: -.02em; color: var(--text); line-height: 1; }
.lr-brand-sub { font-size: 10px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase; color: var(--muted); margin-top: 3px; }
.lr-hero {
  flex: 1; display: flex; flex-direction: column; justify-content: center;
  position: relative; z-index: 1; padding: 48px 0 32px;
}
.lr-eyebrow {
  font-size: 10px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase;
  color: var(--accent); margin-bottom: 18px; display: flex; align-items: center; gap: 8px;
}
.lr-eyebrow::before { content: ''; display: block; width: 20px; height: 2px; background: var(--accent); border-radius: 1px; }
.lr-headline { font-size: 38px; font-weight: 800; line-height: 1.12; letter-spacing: -.03em; color: var(--text); margin-bottom: 20px; text-wrap: balance; }
.lr-headline mark {
  background: linear-gradient(90deg, #2563eb, #6d28d9);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text; font-style: normal;
}
.lr-desc { font-size: 14px; line-height: 1.75; color: var(--muted); max-width: 380px; margin-bottom: 40px; }
.lr-features { display: flex; flex-direction: column; gap: 3px; }
.lr-feat {
  display: flex; align-items: flex-start; gap: 14px; padding: 12px 14px;
  border-radius: 8px; border: 1px solid transparent; transition: background .15s, border-color .15s;
}
.lr-feat:hover { background: rgba(64,119,244,.05); border-color: rgba(64,119,244,.1); }
.lr-feat-icon { font-size: 16px; line-height: 1; margin-top: 1px; flex-shrink: 0; }
.lr-feat-label { font-size: 13px; font-weight: 600; color: var(--text2); line-height: 1.2; }
.lr-feat-desc { font-size: 11px; color: var(--muted); margin-top: 2px; line-height: 1.4; }
.lr-footer { font-size: 11px; color: var(--muted); opacity: .5; position: relative; z-index: 1; }

/* RIGHT */
.lr-right {
  flex: 1; display: flex; align-items: center; justify-content: center;
  padding: 40px 24px; position: relative; background: var(--bg);
}
.lr-right::before {
  content: ''; position: absolute; inset: 0;
  background-image: radial-gradient(circle, rgba(64,119,244,.18) 1px, transparent 1px);
  background-size: 32px 32px;
  mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 20%, transparent 100%);
  -webkit-mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 20%, transparent 100%);
  pointer-events: none;
}
.lr-card {
  width: 100%; max-width: 400px;
  background: var(--card); border: 1px solid var(--border); border-radius: 16px;
  padding: 44px 40px; position: relative;
  box-shadow: 0 0 0 1px rgba(64,119,244,.08), 0 24px 60px rgba(0,0,0,.5), 0 4px 16px rgba(0,0,0,.3);
}
.lr-card::before {
  content: ''; position: absolute; top: 0; left: 40px; right: 40px; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(64,119,244,.6), rgba(109,94,245,.6), transparent);
}
.lr-card-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--muted); margin-bottom: 10px; }
.lr-card-title { font-size: 24px; font-weight: 700; letter-spacing: -.025em; color: var(--text); margin-bottom: 8px; line-height: 1.2; }
.lr-card-sub { font-size: 13px; color: var(--muted); line-height: 1.65; margin-bottom: 36px; }
.lr-google-wrap { display: flex; justify-content: center; }
.lr-divider { display: flex; align-items: center; gap: 12px; margin: 22px 0; font-size: 11px; color: var(--muted); text-transform: uppercase; letter-spacing: .06em; }
.lr-divider::before, .lr-divider::after { content: ''; flex: 1; height: 1px; background: var(--border); }
.lr-alt-link { display: block; width: 100%; background: none; border: none; color: var(--accent); font-size: 13px; font-weight: 600; cursor: pointer; text-align: center; padding: 4px; font-family: inherit; }
.lr-field { margin-bottom: 16px; }
.lr-label { display: block; font-size: 11px; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; color: var(--muted); margin-bottom: 6px; }
.lr-input { width: 100%; box-sizing: border-box; padding: 11px 14px; font-size: 14px; border: 1px solid var(--border); border-radius: 8px; background: #fff; color: var(--text); font-family: inherit; }
.lr-input:focus { outline: none; border-color: var(--accent); }
.lr-submit { width: 100%; padding: 11px; font-size: 14px; font-weight: 600; color: #fff; background: var(--accent); border: none; border-radius: 8px; cursor: pointer; font-family: inherit; }
.lr-submit:disabled { opacity: .6; cursor: default; }
.lr-notice { background: rgba(5,150,105,.08); border: 1px solid rgba(5,150,105,.2); border-radius: 8px; padding: 11px 14px; font-size: 13px; color: #059669; margin-bottom: 20px; line-height: 1.5; }
.lr-back-link { display: block; width: 100%; background: none; border: none; color: var(--muted); font-size: 12.5px; cursor: pointer; margin-top: 14px; padding: 0; font-family: inherit; text-align: center; }
.lr-error {
  background: rgba(239,68,68,.07); border: 1px solid rgba(239,68,68,.2);
  border-radius: 8px; padding: 11px 14px; font-size: 13px; color: #dc2626;
  margin-bottom: 20px; line-height: 1.5;
}
.lr-trust {
  display: flex; align-items: center; justify-content: center; gap: 18px;
  margin-top: 28px; padding-top: 24px; border-top: 1px solid var(--border2);
}
.lr-trust-item { display: flex; align-items: center; gap: 5px; font-size: 11px; color: var(--muted); opacity: .65; }
.lr-trust-dot { width: 5px; height: 5px; border-radius: 50%; background: #059669; flex-shrink: 0; }
.lr-secure { text-align: center; font-size: 11px; color: var(--muted); margin-top: 16px; opacity: .45; line-height: 1.6; }
@media (max-width: 820px) { .lr-left { display: none; } }
`;

export default function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Google is the default path; email OTP is an alternative for accounts
  // that aren't a Google login (most non-Gmail company addresses).
  const [mode, setMode] = useState("google"); // "google" | "otp"
  const [otpStep, setOtpStep] = useState("email"); // "email" | "code"
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleSuccess = async (credentialResponse) => {
    setError("");
    try {
      const res = await api.post("/auth/google", { id_token: credentialResponse.credential });
      login(res.data.user, res.data.token);
      navigate("/");
    } catch (e) {
      setError(e.response?.data?.detail || "Authentication failed. Please try again.");
    }
  };

  const requestOtp = async () => {
    if (!otpEmail.trim()) { setError("Enter your email address."); return; }
    setSending(true); setError(""); setNotice("");
    try {
      await api.post("/auth/otp/request", { email: otpEmail.trim() });
      setOtpStep("code");
      setNotice("If that email has an account, a login code was just sent to it.");
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't send the code. Try again.");
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpCode.trim()) { setError("Enter the code from your email."); return; }
    setVerifying(true); setError("");
    try {
      const res = await api.post("/auth/otp/verify", { email: otpEmail.trim(), code: otpCode.trim() });
      login(res.data.user, res.data.token);
      navigate("/");
    } catch (e) {
      setError(e.response?.data?.detail || "That code didn't work. Try again.");
    } finally {
      setVerifying(false);
    }
  };

  const switchMode = (m) => {
    setMode(m); setError(""); setNotice(""); setOtpStep("email"); setOtpEmail(""); setOtpCode("");
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="lr">

        <div className="lr-left">
          <div className="lr-lines" />
          <div className="lr-brand">
            <div className="lr-brand-mark">&#129302;</div>
            <div>
              <div className="lr-brand-name">AI Agent Portal</div>
              <div className="lr-brand-sub">by Lyqa Tech Ventures</div>
            </div>
          </div>

          <div className="lr-hero">
            <h1 className="lr-headline">
              Your workflow,<br /><mark>intelligently</mark><br />automated.
            </h1>
            <p className="lr-desc">
              A suite of purpose-built AI agents for business operations &mdash;
              handling everything from contact intelligence to bid compliance.
            </p>
            <div className="lr-features">
              {FEATURES.map(f => (
                <div className="lr-feat" key={f.label}>
                  <span className="lr-feat-icon">{f.icon}</span>
                  <div>
                    <div className="lr-feat-label">{f.label}</div>
                    <div className="lr-feat-desc">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lr-footer">&copy; 2025 AI Agent Portal &middot; All rights reserved</div>
        </div>

        <div className="lr-right">
          <div className="lr-card">
            <div className="lr-card-eyebrow">Welcome back</div>
            <h2 className="lr-card-title">Sign in to your account</h2>
            <p className="lr-card-sub">
              {mode === "google"
                ? "Use your Google account to access the portal."
                : "Enter your email and we'll send you a one-time login code."}
            </p>

            {error && <div className="lr-error">&#9888;&nbsp; {error}</div>}
            {notice && mode === "otp" && otpStep === "code" && <div className="lr-notice">{notice}</div>}

            {mode === "google" ? (
              <>
                <div className="lr-google-wrap">
                  <GoogleLogin
                    onSuccess={handleSuccess}
                    onError={() => setError("Google sign-in failed. Please try again.")}
                    useOneTap
                    theme="filled_black"
                    size="large"
                    text="signin_with"
                    shape="rectangular"
                    width="320"
                  />
                </div>
                <div className="lr-divider">or</div>
                <button className="lr-alt-link" onClick={() => switchMode("otp")}>
                  Sign in with email instead
                </button>
              </>
            ) : otpStep === "email" ? (
              <>
                <div className="lr-field">
                  <label className="lr-label">Email Address</label>
                  <input className="lr-input" type="email" value={otpEmail} placeholder="you@company.com"
                         onChange={e => setOtpEmail(e.target.value)}
                         onKeyDown={e => e.key === "Enter" && requestOtp()} autoFocus />
                </div>
                <button className="lr-submit" disabled={sending} onClick={requestOtp}>
                  {sending ? "Sending…" : "Send Code"}
                </button>
                <button className="lr-back-link" onClick={() => switchMode("google")}>
                  &larr; Back to Google Sign-In
                </button>
              </>
            ) : (
              <>
                <div className="lr-field">
                  <label className="lr-label">Login Code</label>
                  <input className="lr-input" value={otpCode} placeholder="6-digit code" maxLength={6}
                         onChange={e => setOtpCode(e.target.value.replace(/\D/g, ""))}
                         onKeyDown={e => e.key === "Enter" && verifyOtp()} autoFocus />
                </div>
                <button className="lr-submit" disabled={verifying} onClick={verifyOtp}>
                  {verifying ? "Verifying…" : "Verify & Sign In"}
                </button>
                <button className="lr-back-link" onClick={() => { setOtpStep("email"); setOtpCode(""); setError(""); setNotice(""); }}>
                  Use a different email
                </button>
              </>
            )}

            <div className="lr-trust">
              <div className="lr-trust-item"><span className="lr-trust-dot" />OAuth 2.0 Secured</div>
              <div className="lr-trust-item"><span className="lr-trust-dot" />No passwords stored</div>
              <div className="lr-trust-item"><span className="lr-trust-dot" />TLS encrypted</div>
            </div>

            <p className="lr-secure">
              Access is restricted to authorised organisation accounts only.
            </p>
          </div>
        </div>

      </div>
    </>
  );
}
